import React, { useState, useEffect, useContext, useCallback } from 'react';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle, CheckCircle2, Loader2, Eye, Save,
  Mail, CreditCard, Phone, RefreshCw, RotateCcw
} from 'lucide-react';
import {
  getPlatformEmailTemplates,
  savePlatformEmailTemplate,
  previewPlatformEmailTemplate,
  resetPlatformEmailTemplate,
} from '../services/api';


// ── Template catalogue ───────────────────────────────────────────────────────

const TEMPLATE_GROUPS = [
  {
    id: 'subscription',
    label: 'Subscription',
    icon: RefreshCw,
    color: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    activeColor: 'bg-emerald-600 text-white',
    types: [
      {
        value: 'SUBSCRIPTION_CREATED',
        label: 'Subscription Activated',
        description: 'Sent when a new subscription becomes active after checkout.',
        placeholders: ['{{subscriptionPlan}}', '{{subscriptionAmount}}', '{{subscriptionFreq}}',
                       '{{startDate}}', '{{coveredCallAmount}}', '{{organizationName}}'],
      },
      {
        value: 'SUBSCRIPTION_CANCELLED',
        label: 'Subscription Cancelled',
        description: 'Sent when the customer cancels — confirms it stays active until period end.',
        placeholders: ['{{subscriptionPlan}}', '{{cancellationReason}}', '{{organizationName}}'],
      },
      {
        value: 'SUBSCRIPTION_UPGRADED',
        label: 'Subscription Upgraded',
        description: 'Sent when the customer moves to a higher plan.',
        placeholders: ['{{previousPlan}}', '{{subscriptionPlan}}', '{{subscriptionAmount}}',
                       '{{startDate}}', '{{coveredCallAmount}}', '{{organizationName}}'],
      },
      {
        value: 'SUBSCRIPTION_REACTIVATED',
        label: 'Subscription Reactivated',
        description: 'Sent when a previously cancelled subscription is resumed.',
        placeholders: ['{{subscriptionPlan}}', '{{subscriptionAmount}}', '{{startDate}}',
                       '{{organizationName}}'],
      },
    ],
  },
  {
    id: 'payment',
    label: 'Payments',
    icon: CreditCard,
    color: 'bg-blue-50 border-blue-200 text-blue-800',
    activeColor: 'bg-blue-600 text-white',
    types: [
      {
        value: 'PAYMENT_SUCCEEDED',
        label: 'Payment Received',
        description: 'Sent on every successful subscription invoice payment.',
        placeholders: ['{{amount}}', '{{description}}', '{{paymentDate}}',
                       '{{paymentIntentId}}', '{{organizationName}}'],
      },
      {
        value: 'PAYMENT_FAILED',
        label: 'Payment Failed',
        description: 'Sent when a subscription invoice payment fails — prompts action.',
        placeholders: ['{{amount}}', '{{reason}}', '{{paymentDate}}', '{{organizationName}}'],
      },
      {
        value: 'TOPUP_SUCCEEDED',
        label: 'Top-up Successful',
        description: 'Sent after an automatic call-credit top-up charges successfully.',
        placeholders: ['{{amount}}', '{{paymentDate}}', '{{paymentIntentId}}',
                       '{{organizationName}}'],
      },
      {
        value: 'TOPUP_FAILED',
        label: 'Top-up Failed',
        description: 'Sent when an automatic top-up charge fails — prompts the customer to act.',
        placeholders: ['{{amount}}', '{{reason}}', '{{paymentDate}}', '{{organizationName}}'],
      },
      {
        value: 'REFUND_ISSUED',
        label: 'Refund Issued',
        description: 'Sent when a refund is processed back to the original payment method.',
        placeholders: ['{{amount}}', '{{reason}}', '{{paymentDate}}', '{{organizationName}}'],
      },
    ],
  },
  {
    id: 'calls',
    label: 'Call Summary',
    icon: Phone,
    color: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    activeColor: 'bg-indigo-600 text-white',
    types: [
      {
        value: 'CALL_SUMMARY',
        label: 'Call Summary',
        description: 'Sent after every AI call completes — includes transcript and summary.',
        placeholders: ['{{callId}}', '{{callerNumber}}', '{{assistantName}}', '{{callDuration}}',
                       '{{callTimestamp}}', '{{summary}}', '{{transcript}}', '{{endedReason}}',
                       '{{recordingUrl}}', '{{organizationName}}'],
      },
    ],
  },
];

const ALL_TYPES = TEMPLATE_GROUPS.flatMap(g => g.types.map(t => ({ ...t, group: g })));


// ── Placeholder chip bar ──────────────────────────────────────────────────────

const PlaceholderBar = ({ placeholders, textareaId, onInsert }) => (
  <div className="flex flex-wrap gap-1.5 mt-2">
    {placeholders.map(ph => (
      <button
        key={ph}
        type="button"
        onClick={() => onInsert(ph, textareaId)}
        className="px-2 py-0.5 rounded text-xs font-mono bg-slate-100 text-slate-700
                   border border-slate-200 hover:bg-slate-200 transition-colors"
      >
        {ph}
      </button>
    ))}
  </div>
);


// ── Main component ────────────────────────────────────────────────────────────

const PlatformEmailTemplateEditor = () => {
  const { organizationId } = useContext(OrganizationContext);

  // Template data keyed by type string
  const [templates, setTemplates]     = useState({});
  const [activeType, setActiveType]   = useState('SUBSCRIPTION_CREATED');
  const [activeGroup, setActiveGroup] = useState('subscription');

  const [isLoading, setIsLoading]     = useState(false);
  const [isSaving, setIsSaving]       = useState(false);
  const [message, setMessage]         = useState({ type: '', content: '' });

  const activeMeta = ALL_TYPES.find(t => t.value === activeType);


  // ── Data fetching ───────────────────────────────────────────────────────────

  const fetchTemplates = useCallback(async () => {
    if (!organizationId) return;
    try {
      setIsLoading(true);
      const data = await getPlatformEmailTemplates(organizationId);
      // data is an object keyed by type, e.g. { SUBSCRIPTION_CREATED: { subject, htmlContent, id } }
      setTemplates(data || {});
    } catch (err) {
      setMessage({ type: 'error', content: 'Failed to load templates.' });
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);


  // ── Field helpers ───────────────────────────────────────────────────────────

  const handleFieldChange = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [activeType]: { ...(prev[activeType] || {}), [field]: value },
    }));
    setMessage({ type: '', content: '' });
  };

  const insertPlaceholder = (placeholder, textareaId) => {
    const el = document.getElementById(textareaId);
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const val   = el.value;
    const next  = val.substring(0, start) + placeholder + val.substring(end);
    handleFieldChange('htmlContent', next);
    setTimeout(() => {
      el.selectionStart = start + placeholder.length;
      el.selectionEnd   = start + placeholder.length;
      el.focus();
    }, 0);
  };


  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const tpl = templates[activeType];
    if (!tpl?.subject?.trim() || !tpl?.htmlContent?.trim()) {
      setMessage({ type: 'error', content: 'Subject and HTML content are required.' });
      return;
    }
    try {
      setIsSaving(true);
      const saved = await savePlatformEmailTemplate(organizationId, {
        type: activeType,
        subject: tpl.subject,
        htmlContent: tpl.htmlContent,
      });
      setTemplates(prev => ({ ...prev, [activeType]: saved }));
      setMessage({ type: 'success', content: 'Template saved.' });
    } catch (err) {
      setMessage({ type: 'error', content: err.message || 'Save failed.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const tpl = templates[activeType] || {};
      const html = await previewPlatformEmailTemplate(organizationId, {
        type: activeType,
        subject: tpl.subject || '',
        htmlContent: tpl.htmlContent || '',
      });
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
    } catch (err) {
      setMessage({ type: 'error', content: 'Preview failed.' });
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to the built-in default? Your custom template will be deleted.')) return;
    try {
      await resetPlatformEmailTemplate(organizationId, activeType);
      setTemplates(prev => {
        const next = { ...prev };
        delete next[activeType];
        return next;
      });
      setMessage({ type: 'success', content: 'Reset to default template.' });
    } catch (err) {
      setMessage({ type: 'error', content: 'Reset failed.' });
    }
  };

  const switchType = (type, groupId) => {
    setActiveType(type);
    setActiveGroup(groupId);
    setMessage({ type: '', content: '' });
  };

  const currentTemplate = templates[activeType] || {};
  const isCustomised    = !!currentTemplate.id;   // has a saved record in DB


  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Platform Email Templates</h1>
        <p className="text-sm text-slate-500 mt-1">
          Customise the emails sent to customers for subscription, payment, and call events.
          Leave a template at its default — built-in templates are always used as the fallback.
        </p>
      </div>

      <div className="grid grid-cols-[240px_1fr] gap-4 items-start">

        {/* ── Left nav ──────────────────────────────────────────────────────── */}
        <Card className="sticky top-4">
          <CardContent className="p-3 space-y-1">
            {TEMPLATE_GROUPS.map(group => {
              const Icon = group.icon;
              const isGroupActive = activeGroup === group.id;
              return (
                <div key={group.id}>
                  <div className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs
                                   font-semibold uppercase tracking-wide mb-1
                                   ${isGroupActive ? 'text-slate-900' : 'text-slate-400'}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {group.label}
                  </div>
                  {group.types.map(type => {
                    const isActive  = activeType === type.value;
                    const isSaved   = !!templates[type.value]?.id;
                    return (
                      <button
                        key={type.value}
                        onClick={() => switchType(type.value, group.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm
                                    flex items-center justify-between gap-2 transition-colors
                                    ${isActive
                                      ? 'bg-slate-900 text-white'
                                      : 'text-slate-600 hover:bg-slate-100'}`}
                      >
                        <span className="truncate">{type.label}</span>
                        {isSaved && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0
                                           ${isActive
                                             ? 'bg-white/20 text-white'
                                             : 'bg-emerald-100 text-emerald-700'}`}>
                            Custom
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {group.id !== 'calls' && <Separator className="my-2" />}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* ── Editor panel ──────────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Type header */}
          <div className={`rounded-lg border px-4 py-3 ${activeMeta?.group?.color || ''}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-sm">{activeMeta?.label}</p>
                <p className="text-xs mt-0.5 opacity-80">{activeMeta?.description}</p>
              </div>
              {isCustomised
                ? <Badge variant="secondary" className="shrink-0 text-xs">Custom template</Badge>
                : <Badge variant="outline" className="shrink-0 text-xs opacity-70">Using default</Badge>
              }
            </div>
          </div>

          {/* Status message */}
          {message.content && (
            <Alert className={message.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}>
              {message.type === 'error'
                ? <AlertCircle className="h-4 w-4 text-red-600" />
                : <CheckCircle2 className="h-4 w-4 text-green-600" />}
              <AlertDescription className={message.type === 'error' ? 'text-red-700' : 'text-green-700'}>
                {message.content}
              </AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading templates…
            </div>
          ) : (
            <Card>
              <CardContent className="p-5 space-y-5">

                {/* Subject */}
                <div>
                  <Label htmlFor="subject" className="text-sm font-medium">
                    Subject line
                  </Label>
                  <Input
                    id="subject"
                    className="mt-1 font-mono text-sm"
                    value={currentTemplate.subject || ''}
                    placeholder="e.g. ✅ Subscription Activated — {{subscriptionPlan}}"
                    onChange={e => handleFieldChange('subject', e.target.value)}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Placeholders work here too — e.g. <code>{'{{subscriptionPlan}}'}</code>
                  </p>
                </div>

                {/* Placeholders */}
                <div>
                  <Label className="text-sm font-medium">Available placeholders</Label>
                  <PlaceholderBar
                    placeholders={activeMeta?.placeholders || []}
                    textareaId="htmlContent"
                    onInsert={insertPlaceholder}
                  />
                </div>

                {/* HTML content */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="htmlContent" className="text-sm font-medium">
                      HTML content
                    </Label>
                    <span className="text-xs text-slate-400">
                      {(currentTemplate.htmlContent || '').length} chars
                    </span>
                  </div>
                  <Textarea
                    id="htmlContent"
                    className="min-h-[360px] font-mono text-xs leading-relaxed"
                    value={currentTemplate.htmlContent || ''}
                    placeholder="Leave blank to use the built-in default template."
                    onChange={e => handleFieldChange('htmlContent', e.target.value)}
                  />
                </div>

                {/* Action bar */}
                <div className="flex items-center justify-between pt-1">
                  <div className="flex gap-2">
                    <Button onClick={handleSave} disabled={isSaving} size="sm">
                      {isSaving
                        ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</>
                        : <><Save className="w-3.5 h-3.5 mr-1.5" />Save template</>}
                    </Button>
                    <Button variant="outline" onClick={handlePreview} size="sm">
                      <Eye className="w-3.5 h-3.5 mr-1.5" />Preview
                    </Button>
                  </div>
                  {isCustomised && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-red-600"
                      onClick={handleReset}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                      Reset to default
                    </Button>
                  )}
                </div>

              </CardContent>
            </Card>
          )}

          {/* Info callout */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-500">
            <strong className="text-slate-700">How this works:</strong> when an event fires,
            the system looks for a custom template saved here first. If none exists,
            it falls back to the built-in default — so every event always sends a professional email
            without any setup required. Customise only the templates you want to brand.
          </div>

        </div>
      </div>
    </div>
  );
};

export default PlatformEmailTemplateEditor;